/** @jsxImportSource @gasman/smush */

import './grid.css';

var removeChildren = require('remove-children');

class Grid {
    GRID_KEY_MAPPINGS = {
        'val:ArrowRight': { action: 'moveCursorRight' },
        'val:ArrowLeft': { action: 'moveCursorLeft' },
        'val:ArrowUp': { action: 'moveCursorUp' },
        'val:ArrowDown': { action: 'moveCursorDown' },
    };

    constructor(container, opts) {
        /* recognised opts:
        rows, columns
        rowLabels, columnLabels
        className
        getValue(x, y)
        onkeydown((e) => {})
        ondblclick((e, x, y) => {})
        */
        this.opts = opts || {};

        this.rowCount = this.opts.rows || 1;
        this.columnCount = this.opts.columns || 1;

        let className = 'grid';
        if (this.opts.className) {
            className += ' ' + this.opts.className;
        }

        this.table = <table class={className}></table>;
        container.appendChild(this.table);

        this.table.onkeydown = (e) => {
            var keyCode = 'key:' + e.code;
            var keyVal = 'val:' + e.key;
            var action =
                this.GRID_KEY_MAPPINGS[keyCode] ||
                this.GRID_KEY_MAPPINGS[keyVal];
            if (action) {
                this[action.action]();
                return false;
            } else if (this.opts.onkeydown) {
                return this.opts.onkeydown(e);
            }
        };

        this.table.ondblclick = (e) => {
            if (this.opts.ondblclick) {
                let cursorPosition = this.getCursorPosition();
                if (cursorPosition) {
                    return this.opts.ondblclick(
                        e,
                        cursorPosition[0],
                        cursorPosition[1],
                    );
                }
            }
        };

        this.columnHeaders = [];
        if (this.opts.columnLabels) {
            this.headerRow = <tr></tr>;
            this.table.appendChild(this.headerRow);
            if (this.opts.rowLabels) {
                this.headerRow.appendChild(<th></th>);
            }
            this.opts.columnLabels.forEach((label, i) => {
                let header = <th scope="col">{label}</th>;
                this.headerRow.appendChild(header);
                this.columnHeaders[i] = header;
            });
        }

        this.rows = [];
        this.cells = [];
        for (var y = 0; y < this.rowCount; y++) {
            this.cells[y] = [];
            var tr = (this.rows[y] = <tr data-row={y}></tr>);
            this.table.appendChild(tr);
            if (this.opts.rowLabels) {
                tr.appendChild(<th scope="row">{this.opts.rowLabels[y]}</th>);
            }
            for (var x = 0; x < this.columnCount; x++) {
                let val = this.opts.getValue
                    ? this.opts.getValue(x, y).toString()
                    : '';
                var cell = (
                    <td tabindex="0" data-col={x}>
                        {val}
                    </td>
                );
                tr.appendChild(cell);
                this.cells[y][x] = cell;
            }
        }
    }

    setValue(x, y, value) {
        this.cells[y][x].innerText = value.toString();
    }

    getCursorPosition() {
        var cell = document.activeElement;
        while (cell.tagName != 'TD') cell = cell.parentElement;
        var row = cell.parentNode;
        if (row.parentNode == this.table) {
            var x = parseInt(cell.dataset.col);
            var y = parseInt(row.dataset.row);
            return [x, y];
        } else {
            return null;
        }
    }

    moveCursorRight() {
        var x, y;
        [x, y] = this.getCursorPosition();
        x = (x + 1) % this.columnCount;
        this.cells[y][x].focus();
    }
    moveCursorLeft() {
        var x, y;
        [x, y] = this.getCursorPosition();
        x -= 1;
        if (x < 0) x = this.columnCount - 1;
        this.cells[y][x].focus();
    }
    moveCursorDown() {
        var x, y;
        [x, y] = this.getCursorPosition();
        y = (y + 1) % this.rowCount;
        this.cells[y][x].focus();
    }
    moveCursorUp() {
        var x, y;
        [x, y] = this.getCursorPosition();
        y -= 1;
        if (y < 0) y = this.rowCount - 1;
        this.cells[y][x].focus();
    }

    appendColumn(opts) {
        if (this.opts.columnLabels && opts.label) {
            let header = <th scope="col">{opts.label}</th>;
            this.headerRow.appendChild(header);
            this.columnHeaders.push(header);
        }

        let x = this.columnCount;
        for (var y = 0; y < this.rowCount; y++) {
            let tr = this.rows[y];
            let val = this.opts.getValue
                ? this.opts.getValue(x, y).toString()
                : '';
            var cell = (
                <td tabindex="0" data-col={x}>
                    {val}
                </td>
            );
            tr.appendChild(cell);
            this.cells[y][x] = cell;
        }
        this.columnCount++;
    }

    addColumnClass(x, className) {
        for (var y = 0; y < this.rowCount; y++) {
            this.cells[y][x].classList.add(className);
        }
        if (this.opts.columnLabels) {
            this.columnHeaders[x].classList.add(className);
        }
    }
    removeColumnClass(x, className) {
        for (var y = 0; y < this.rowCount; y++) {
            this.cells[y][x].classList.remove(className);
        }
        if (this.opts.columnLabels) {
            this.columnHeaders[x].classList.remove(className);
        }
    }
}

class NumberGrid extends Grid {
    NUMBER_GRID_KEY_MAPPINGS = {
        'val:0': { action: 'inputNumber', keepDefault: true },
        'val:1': { action: 'inputNumber', keepDefault: true },
        'val:2': { action: 'inputNumber', keepDefault: true },
        'val:3': { action: 'inputNumber', keepDefault: true },
        'val:4': { action: 'inputNumber', keepDefault: true },
        'val:5': { action: 'inputNumber', keepDefault: true },
        'val:6': { action: 'inputNumber', keepDefault: true },
        'val:7': { action: 'inputNumber', keepDefault: true },
        'val:8': { action: 'inputNumber', keepDefault: true },
        'val:9': { action: 'inputNumber', keepDefault: true },
        'val:-': { action: 'inputNumber', keepDefault: true },
        'val:+': { action: 'inputNumber', keepDefault: true },
        'val:Enter': { action: 'commitInput' },
    };

    constructor(container, opts) {
        /* opts: same as Grid but with commitValue(x, y, val) instead of onkeydown */
        opts = opts || {};

        super(container, {
            rows: opts.rows,
            columns: opts.columns,
            rowLabels: opts.rowLabels,
            columnLabels: opts.columnLabels,
            className: opts.className,
            getValue: opts.getValue,
            ondblclick: opts.ondblclick,
            onkeydown: (e) => {
                var keyCode = 'key:' + e.code;
                var keyVal = 'val:' + e.key;
                var action =
                    this.NUMBER_GRID_KEY_MAPPINGS[keyCode] ||
                    this.NUMBER_GRID_KEY_MAPPINGS[keyVal];
                if (action) {
                    this[action.action]();
                    return action.keepDefault || false;
                }
            },
            commitValue: opts.commitValue,
        });

        this.input = null;
    }

    commitInput() {
        if (this.input) this.input.parentElement.focus();
    }

    inputNumber() {
        if (this.input) return;

        var x, y;
        [x, y] = this.getCursorPosition();
        var cell = document.activeElement;

        var oldValue = cell.innerText;
        removeChildren(cell);
        this.input = <input></input>;
        cell.appendChild(this.input);
        this.input.focus();
        this.input.addEventListener('blur', () => {
            var val = parseInt(this.input.value);
            cell.innerText = oldValue;
            this.input = null;
            if (!isNaN(val) && this.opts.commitValue) {
                this.opts.commitValue(x, y, val);
            }
        });
    }
}

export { Grid, NumberGrid };
